import React from 'react';
import SectionHeader from './SectionHeader';
import { computeRequestedDocuments } from './requestedDocs';

const SENTIMENT_LABELS = {
  dark_green: 'Great',
  green: 'Good',
  yellow: 'Mixed',
  red: 'Concerned',
};

const FREQUENCY_LABELS = {
  annually: 'Annually',
  quarterly: 'Quarterly',
  monthly: 'Monthly',
  weekly: 'Weekly',
  daily: 'Daily',
};

const FAMILY_LABELS = {
  married: 'Got married',
  divorced: 'Got divorced',
  widowed: 'Lost a spouse',
  new_child: 'New child / grandchild',
  supporting_family: 'Now supporting a family member',
  moved: 'Moved',
};

const CAREER_LABELS = {
  changed_jobs: 'Changed jobs / promotion',
  promoted: 'Change in compensation (bonus, equity, stock options)',
  started_or_sold_business: 'Started or sold a business',
  retired: 'Retired or planning to retire soon',
};

const FINANCIAL_LABELS = {
  new_or_consolidated_accounts: 'Added, consolidated, or transferred accounts',
  new_loan: 'Took out a new loan',
  changed_investment_approach: 'Changed investment approach or preferences',
  interested_in_strategies: 'Interested in strategies',
  large_purchase_or_sale: 'Made a large purchase or sale',
  insurance_changes: 'Purchased or updated insurance',
  estate_changes: 'Updated estate documents or beneficiaries',
  inheritance_or_gift: 'Received an inheritance or large gift',
};

function LifeEventSummary({ data, labels }) {
  if (!data) return <span className="text-muted small">No information provided.</span>;
  if (data.none_of_the_above) return <span className="text-muted small">No changes since last review.</span>;
  const checked = Object.entries(labels).filter(([key]) => data[key]);
  if (checked.length === 0) return <span className="text-muted small">No changes since last review.</span>;
  return (
    <ul className="small mb-0">
      {checked.map(([, label]) => (
        <li key={label}>{label}</li>
      ))}
    </ul>
  );
}

function fmtAnnual(amount) {
  if (amount === null || amount === undefined) return null;
  const n = Number(amount);
  if (isNaN(n) || n === 0) return null;
  return `$${Math.round(n).toLocaleString()}/yr`;
}

export default function StepReview({ formData }) {
  const docs = computeRequestedDocuments(formData || {});

  const renderSection = (key, title, render) => {
    const data = formData?.[key];
    return (
      <div className="cashflow-section-card" key={key}>
        <h6 className="cashflow-subsection-title">{title}</h6>
        {data ? render(data) : <span className="text-muted small">No information provided.</span>}
      </div>
    );
  };

  return (
    <div>
      <SectionHeader
        number={10}
        title="Review Your Answers"
        tip="Take a moment to review your responses. Click Back to make changes."
        showAnnualNote={false}
      />

      {renderSection('personal', 'Personal Information', (d) => (
        <ul className="small mb-0">
          <li><strong>Name:</strong> {d.first_name} {d.last_name}</li>
          {d.has_partner === 'yes' && (
            <li><strong>Partner:</strong> {d.partner_first_name} {d.partner_last_name}</li>
          )}
          <li><strong>Phone:</strong> {d.phone || '—'}</li>
          <li><strong>Email:</strong> {d.email || '—'}</li>
          <li><strong>Uses eMoney:</strong> {d.uses_emoney === 'yes' ? 'Yes' : d.uses_emoney === 'no' ? 'No' : '—'}</li>
          <li><strong>Tracks spending:</strong> {d.tracks_spending === 'yes' ? 'Yes' : d.tracks_spending === 'no' ? 'No' : '—'}{d.tracks_spending === 'yes' && d.spending_method ? ` — ${d.spending_method}` : ''}</li>
          <li><strong>Portfolio check frequency:</strong> {FREQUENCY_LABELS[d.investment_check_frequency] || d.investment_check_frequency || '—'}</li>
          <li><strong>Feeling about investments:</strong> {SENTIMENT_LABELS[d.investment_sentiment] || d.investment_sentiment || '—'}</li>
        </ul>
      ))}

      <div className="cashflow-section-card">
        <h6 className="cashflow-subsection-title">Family &amp; Personal</h6>
        <LifeEventSummary data={formData?.family_personal} labels={FAMILY_LABELS} />
      </div>

      <div className="cashflow-section-card">
        <h6 className="cashflow-subsection-title">Career &amp; Income</h6>
        <LifeEventSummary data={formData?.career_income} labels={CAREER_LABELS} />
      </div>

      <div className="cashflow-section-card">
        <h6 className="cashflow-subsection-title">Financial &amp; Planning</h6>
        <LifeEventSummary data={formData?.financial_planning} labels={FINANCIAL_LABELS} />
      </div>

      {renderSection('accounts', 'Cash & Savings Accounts', (d) => {
        const list = (d.entries || []).filter(
          (e) => e && Object.values(e).some((v) => v !== null && v !== '' && v !== undefined),
        );
        if (list.length === 0) return <span className="text-muted small">No accounts listed.</span>;
        return <span className="small">{list.length} account{list.length !== 1 ? 's' : ''} listed.</span>;
      })}

      {renderSection('income', 'Income & Recurring Sources', (d) => {
        const list = (d.sources || []).filter(
          (s) => s && (s.type || s.description || s.amount),
        );
        if (list.length === 0) return <span className="text-muted small">No income sources listed.</span>;
        return <span className="small">{list.length} source{list.length !== 1 ? 's' : ''} listed.</span>;
      })}

      {renderSection('debts', 'Debts & Loans', (d) => {
        const list = (d.entries || []).filter(
          (e) => e && Object.values(e).some((v) => v !== null && v !== '' && v !== undefined),
        );
        if (list.length === 0) return <span className="text-muted small">No debts listed.</span>;
        return <span className="small">{list.length} debt{list.length !== 1 ? 's' : ''} listed.</span>;
      })}

      {renderSection('expenses', 'New / Planned Expenses', (d) => {
        const parts = [];
        if (d.rents === 'yes' && d.rent_amount) {
          const f = fmtAnnual(d.rent_amount);
          if (f) parts.push(`Rent ${f}`);
        }
        if (d.has_tuition === 'yes') {
          const total = (d.tuition_entries || []).reduce((sum, e) => sum + (Number(e?.amount) || 0), 0);
          const f = fmtAnnual(total);
          if (f) parts.push(`Tuition ${f}`);
        }
        if (d.medical_annual) {
          const f = fmtAnnual(d.medical_annual);
          if (f) parts.push(`Medical ${f}`);
        }
        if (parts.length === 0) return <span className="text-muted small">No expenses entered.</span>;
        return <span className="small">{parts.join(' | ')}</span>;
      })}

      <div className="alert alert-light border-start border-4 border-danger">
        <h6 className="fw-bold mb-2">
          <i className="bi bi-paperclip me-2 text-danger" />
          Documents to upload after submission
        </h6>
        {docs.length === 0 ? (
          <p className="small text-muted mb-0">
            Based on your responses, no additional documents are needed.
          </p>
        ) : (
          <ul className="small mb-0">
            {docs.map((d) => <li key={d}>{d}</li>)}
          </ul>
        )}
      </div>
    </div>
  );
}
